import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash2, Plus, Loader2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const AdminManagement = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('representatives');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin');
      return;
    }
    fetchData();
  }, [activeTab, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'representatives': endpoint = '/representatives'; break;
        case 'articles': endpoint = '/articles'; break;
        case 'podcasts': endpoint = '/podcasts'; break;
        case 'templates': endpoint = '/templates'; break;
        case 'faqs': endpoint = '/faqs'; break;
        case 'tools': endpoint = '/engagement-tools'; break;
        case 'content': 
          // Fetch content from all sections
          const sections = ['legislature', 'process', 'history'];
          const allContent = [];
          for (const section of sections) {
            try {
              const response = await axios.get(`${API}/content/${section}`);
              allContent.push(...response.data);
            } catch (error) {
              console.error(`Failed to fetch ${section} content:`, error);
            }
          }
          setData(allContent);
          setLoading(false);
          return;
        default: endpoint = '/representatives';
      }
      const response = await axios.get(`${API}${endpoint}`);
      setData(response.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'representatives': endpoint = `/representatives/${id}`; break;
        case 'articles': endpoint = `/articles/${id}`; break;
        case 'podcasts': endpoint = `/podcasts/${id}`; break;
        case 'templates': endpoint = `/templates/${id}`; break;
        case 'faqs': endpoint = `/faqs/${id}`; break;
        case 'tools': endpoint = `/engagement-tools/${id}`; break;
        case 'content': endpoint = `/content/${id}`; break;
      }
      
      await axios.delete(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Item deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleSave = async (formData) => {
    try {
      let endpoint = '';
      switch (activeTab) {
        case 'representatives': endpoint = '/representatives'; break;
        case 'articles': endpoint = '/articles'; break;
        case 'podcasts': endpoint = '/podcasts'; break;
        case 'templates': endpoint = '/templates'; break;
        case 'faqs': endpoint = '/faqs'; break;
        case 'tools': endpoint = '/engagement-tools'; break;
        case 'content': endpoint = '/content'; break;
      }

      if (editItem) {
        await axios.put(`${API}${endpoint}/${editItem.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item updated successfully');
      } else {
        await axios.post(`${API}${endpoint}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Item created successfully');
      }
      
      setIsDialogOpen(false);
      setEditItem(null);
      fetchData();
    } catch (error) {
      toast.error(`Failed to ${editItem ? 'update' : 'create'} item`);
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/representatives/bulk-upload`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to upload file';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  return (
    <div className="py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-4xl font-semibold text-slate-900 mb-2">
              Content Management
            </h1>
            <p className="text-slate-600">Manage all platform content</p>
          </div>
          <Button onClick={() => navigate('/admin')} variant="outline">
            ← Back to Dashboard
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 h-auto bg-transparent p-0 mb-8">
            <TabsTrigger value="representatives" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Representatives
            </TabsTrigger>
            <TabsTrigger value="articles" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Articles
            </TabsTrigger>
            <TabsTrigger value="podcasts" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Podcasts
            </TabsTrigger>
            <TabsTrigger value="templates" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Templates
            </TabsTrigger>
            <TabsTrigger value="faqs" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              FAQs
            </TabsTrigger>
            <TabsTrigger value="tools" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Tools
            </TabsTrigger>
            <TabsTrigger value="content" className="rounded-lg border-2 data-[state=active]:border-primary-700">
              Content
            </TabsTrigger>
          </TabsList>

          <div className="mb-4">
            <div className="flex items-center gap-3">
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    onClick={() => setEditItem(null)}
                    className="bg-primary-700 hover:bg-primary-900"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add New {activeTab.slice(0, -1)}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editItem ? 'Edit' : 'Add New'} {activeTab.slice(0, -1)}
                    </DialogTitle>
                    <DialogDescription>
                      Fill in the details below
                    </DialogDescription>
                  </DialogHeader>
                  <FormContent 
                    type={activeTab} 
                    item={editItem} 
                    onSave={handleSave}
                    onCancel={() => {
                      setIsDialogOpen(false);
                      setEditItem(null);
                    }}
                  />
                </DialogContent>
              </Dialog>

              {activeTab === 'representatives' && (
                <div className="relative">
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleBulkUpload}
                    className="hidden"
                    id="bulk-upload"
                    disabled={uploading}
                  />
                  <Button
                    onClick={() => document.getElementById('bulk-upload').click()}
                    disabled={uploading}
                    variant="outline"
                    className="border-primary-700 text-primary-700 hover:bg-primary-50"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <Upload className="mr-2 h-4 w-4" />
                        Bulk Upload Excel
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
            
            {activeTab === 'representatives' && (
              <p className="text-xs text-slate-500 mt-2">
                Tip: Upload an Excel file with columns: Name, State, Constituency, Office Number, Phone Number, Email Address
              </p>
            )}
          </div>

          <TabsContent value={activeTab}>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-700" />
              </div>
            ) : (
              <DataTable 
                data={data} 
                type={activeTab}
                onEdit={(item) => {
                  setEditItem(item);
                  setIsDialogOpen(true);
                }}
                onDelete={handleDelete}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const DataTable = ({ data, type, onEdit, onDelete }) => {
  if (data.length === 0) {
    return (
      <div className="text-center py-12 bg-surface rounded-xl">
        <p className="text-slate-600">No items found. Add your first item above.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {data.map((item) => (
        <Card key={item.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">
                  {item.title || item.name || item.question || 'Untitled'}
                </h3>
                <div className="text-sm text-slate-600 space-y-1">
                  {type === 'representatives' && (
                    <>
                      <p><strong>State:</strong> {item.state}</p>
                      <p><strong>Constituency:</strong> {item.constituency}</p>
                      <p><strong>Phone:</strong> {item.phone}</p>
                      <p><strong>Email:</strong> {item.email}</p>
                    </>
                  )}
                  {type === 'articles' && (
                    <>
                      <p><strong>Author:</strong> {item.author}</p>
                      <p><strong>Category:</strong> {item.category}</p>
                      <p><strong>Read Time:</strong> {item.read_time} min</p>
                    </>
                  )}
                  {type === 'podcasts' && (
                    <>
                      <p><strong>Episode:</strong> {item.episode}</p>
                      <p><strong>Duration:</strong> {item.duration}</p>
                    </>
                  )}
                  {type === 'faqs' && (
                    <>
                      <p><strong>Category:</strong> {item.category}</p>
                      <p className="line-clamp-2">{item.answer}</p>
                    </>
                  )}
                  {type === 'templates' && (
                    <>
                      <p><strong>Category:</strong> {item.category}</p>
                      <p className="line-clamp-2">{item.description}</p>
                    </>
                  )}
                  {type === 'tools' && (
                    <p className="line-clamp-2">{item.description}</p>
                  )}
                  {type === 'content' && (
                    <>
                      <p><strong>Section:</strong> {item.section}</p>
                      <p className="line-clamp-3">{item.content}</p>
                    </>
                  )}
                </div>
              </div>
              <div className="flex space-x-2 ml-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(item)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onDelete(item.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const FormContent = ({ type, item, onSave, onCancel }) => {
  const [formData, setFormData] = useState(item || {});

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const renderForm = () => {
    switch (type) {
      case 'representatives':
        return (
          <>
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>State *</Label>
              <Input
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Constituency *</Label>
              <Input
                value={formData.constituency || ''}
                onChange={(e) => setFormData({ ...formData, constituency: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Office Number</Label>
              <Input
                value={formData.office_no || ''}
                onChange={(e) => setFormData({ ...formData, office_no: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
          </>
        );

      case 'articles':
        return (
          <>
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Author *</Label>
              <Input
                value={formData.author || ''}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Input
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., Legislative Education"
                required
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Read Time (minutes) *</Label>
              <Input
                type="number"
                value={formData.read_time || 5}
                onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) })}
                required
              />
            </div>
            <div>
              <Label>Image URL</Label>
              <Input
                value={formData.image_url || ''}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://..."
              />
            </div>
            <div>
              <Label>Excerpt *</Label>
              <Textarea
                value={formData.excerpt || ''}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content || ''}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                required
              />
            </div>
          </>
        );

      case 'podcasts':
        return (
          <>
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Episode Number *</Label>
              <Input
                type="number"
                value={formData.episode || ''}
                onChange={(e) => setFormData({ ...formData, episode: parseInt(e.target.value) })}
                required
              />
            </div>
            <div>
              <Label>YouTube URL *</Label>
              <Input
                value={formData.youtube_url || ''}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/embed/..."
                required
              />
            </div>
            <div>
              <Label>Duration *</Label>
              <Input
                value={formData.duration || ''}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="25:30"
                required
              />
            </div>
            <div>
              <Label>Date *</Label>
              <Input
                type="date"
                value={formData.date || ''}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={5}
                required
              />
            </div>
          </>
        );

      case 'faqs':
        return (
          <>
            <div>
              <Label>Category *</Label>
              <Select 
                value={formData.category || ''}
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="bills">Bills</SelectItem>
                  <SelectItem value="committees">Committees</SelectItem>
                  <SelectItem value="legislators">Legislators</SelectItem>
                  <SelectItem value="access">Access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Question *</Label>
              <Textarea
                value={formData.question || ''}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                rows={2}
                required
              />
            </div>
            <div>
              <Label>Answer *</Label>
              <Textarea
                value={formData.answer || ''}
                onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                rows={5}
                required
              />
            </div>
          </>
        );

      case 'templates':
        return (
          <>
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Category *</Label>
              <Input
                value={formData.category || ''}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                placeholder="e.g., petitions, memoranda"
                required
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Content/Template *</Label>
              <Textarea
                value={formData.content || ''}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                placeholder="Enter the template content here..."
                required
              />
            </div>
          </>
        );

      case 'content':
        return (
          <>
            <div>
              <Label>Section *</Label>
              <Select 
                value={formData.section || ''}
                onValueChange={(value) => setFormData({ ...formData, section: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select section" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="legislature">Understanding the Legislature</SelectItem>
                  <SelectItem value="process">Legislative Process</SelectItem>
                  <SelectItem value="committees">Committees</SelectItem>
                  <SelectItem value="history">History</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Order</Label>
              <Input
                type="number"
                value={formData.order || 0}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
              />
            </div>
            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content || ''}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                required
              />
            </div>
          </>
        );

      case 'tools':
        return (
          <>
            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                required
              />
            </div>
            <div>
              <Label>Icon Name</Label>
              <Select 
                value={formData.icon || 'FileText'}
                onValueChange={(value) => setFormData({ ...formData, icon: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="FileText">FileText</SelectItem>
                  <SelectItem value="Users">Users</SelectItem>
                  <SelectItem value="MessageSquare">MessageSquare</SelectItem>
                  <SelectItem value="DollarSign">DollarSign</SelectItem>
                  <SelectItem value="Search">Search</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Steps (one per line) *</Label>
              <Textarea
                value={formData.steps ? formData.steps.join('\n') : ''}
                onChange={(e) => setFormData({ ...formData, steps: e.target.value.split('\n').filter(s => s.trim()) })}
                rows={8}
                placeholder="Step 1: Do this\nStep 2: Do that\nStep 3: Finally this"
                required
              />
            </div>
          </>
        );

      default:
        return <p>Form not available for this type</p>;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {renderForm()}
      <div className="flex justify-end space-x-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" className="bg-primary-700 hover:bg-primary-900">
          {item ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
};

export default AdminManagement;